"use client";

import type { JSX } from "react";
import { useLayoutEffect, useRef } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

// The second card comes to rest 10% of the first card's 440px height below its
// top edge, leaving that strip of the first card visible behind the stack.
// Keep in sync with the `motion-safe:lg:` offsets on the stack markup below.
const CARD_PEEK = 44;

const headingVariants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] as const },
  },
};

const newCompanyBenefits = [
  "Start without confusing paperwork",
  "Get the right structure from day one",
  "Know exactly what comes next",
];

const establishedBusinessBenefits = [
  "Keep records clean and easy to manage",
  "Stay ahead of tax and filing deadlines",
  "Know what needs attention next",
];

interface BenefitListProps {
  benefits: string[];
}

const BenefitList = ({ benefits }: BenefitListProps): JSX.Element => (
  <ul className="flex flex-col gap-2" aria-label="Benefits">
    {benefits.map((benefit) => (
      <li
        key={benefit}
        className="flex items-center gap-2 text-sm leading-5 sm:text-lg sm:leading-6 text-white [font-family:'Suisse_Intl_Trial-Regular',Helvetica]"
      >
        <span className="h-[11px] w-[11px] shrink-0 rounded-full bg-[linear-gradient(147deg,rgba(11,60,255,1)_26%,rgba(88,111,205,1)_63%,rgba(255,255,255,1)_94%)]" />
        <span>{benefit}</span>
      </li>
    ))}
  </ul>
);

const CompanyPreview = (): JSX.Element => (
  <div className="relative h-auto w-full shrink-0 lg:h-[473px] lg:w-[648px] lg:pt-[41px]">
    {/* Below `lg:` this wrapper is only `lg:absolute` — i.e. still in normal
        flow — so the image's own box has to reserve its final height itself.
        `width`/`height` attributes (not just the Tailwind display-size
        classes) tell the browser figure.png's intrinsic 1130x801 aspect
        ratio before the bytes arrive, so `h-auto` computes the right height
        immediately instead of collapsing to 0 and reflowing once it loads —
        a shift that, at that width, also moves BusinessSupportOptionsSection
        (and its own ScrollTrigger) further down the page. */}
    <img
      className="relative left-0 mx-auto block h-auto w-full max-w-[500px] lg:absolute lg:top-[41px] lg:mx-0 lg:h-[401px] lg:w-[565px] lg:max-w-none"
      alt=""
      src="/home-v3/figure.png"
      width={1130}
      height={801}
    />
  </div>
);

const TransactionPreview = (): JSX.Element => (
  <div className="relative h-auto w-full shrink-0 lg:h-[473px] lg:w-[648px] lg:pt-[41px]">
    {/* Same reasoning as figure.png above, for figure2.png's 1136x692. */}
    <img
      className="relative left-0 mx-auto block h-auto w-full max-w-[500px] lg:absolute lg:top-[83px] lg:mx-0 lg:h-[344px] lg:w-[565px] lg:max-w-none"
      alt=""
      src="/home-v3/figure2.png"
      width={1136}
      height={692}
    />
  </div>
);

interface OverviewCardProps {
  title: string;
  description: string;
  benefits: string[];
  buttonLabel: string;
  preview: JSX.Element;
  titleClassName: string;
}

const OverviewCard = ({
  title,
  description,
  benefits,
  buttonLabel,
  preview,
  titleClassName,
}: OverviewCardProps): JSX.Element => (
  <Card className="relative h-full overflow-hidden rounded-[32px] border-0 bg-[#0e1218] p-0 shadow-none">
    <CardContent className="relative flex h-full flex-col items-start gap-5 p-6 pb-44 sm:gap-8 sm:p-8 sm:pb-72 lg:flex-row lg:gap-0 lg:p-10">
      <div className="relative z-10 flex w-full flex-col gap-5 pt-0 sm:gap-8 lg:w-[593px]">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex flex-col gap-[11px]">
            <h2
              className={`${titleClassName} [font-family:'Suisse_Intl_Trial-Medium',Helvetica] font-medium tracking-[-0.96px] text-white`}
            >
              {title}
            </h2>
            <p className="max-w-[493px] [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-sm leading-5 sm:text-lg sm:leading-6 text-white">
              {description}
            </p>
          </div>
          <BenefitList benefits={benefits} />
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-11 w-fit rounded-full border-[#0061fe] bg-transparent px-6 sm:h-14 sm:px-9 [box-shadow:inset_0_0_0_2px_#0061fe] [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-base font-normal text-[#0061fe] hover:bg-[#0061fe] hover:text-white sm:text-xl"
        >
          {buttonLabel}
        </Button>
      </div>
      <div className="absolute bottom-0 right-0 w-[75%] max-w-[340px] lg:bottom-auto lg:right-auto lg:left-[calc(50%_+_47px)] lg:top-0 lg:w-auto lg:max-w-none">
        {preview}
      </div>
    </CardContent>
  </Card>
);

export const BusinessOrganizationOverviewSection = (): JSX.Element => {
  const sectionRef = useRef<HTMLElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const firstCardRef = useRef<HTMLDivElement>(null);
  const secondCardRef = useRef<HTMLDivElement>(null);

  // useLayoutEffect (not useEffect): this creates a ScrollTrigger `pin`, which
  // reparents the pinned DOM node into a GSAP-created `.pin-spacer` div,
  // outside of React's knowledge. useEffect's cleanup for an unmounting
  // component runs asynchronously, *after* React has already removed the old
  // DOM subtree from its recorded parent — but if the pin-spacer is still in
  // place at that point, the real parent no longer matches what React
  // expects, and `parent.removeChild(node)` throws "The node to be removed
  // is not a child of this node." useLayoutEffect's cleanup runs
  // synchronously as part of the same commit, before that removal, so
  // ScrollTrigger has already unwrapped the spacer by the time React touches
  // the DOM.
  useLayoutEffect(() => {
    const section = sectionRef.current;
    const stack = stackRef.current;
    const firstCard = firstCardRef.current;
    const secondCard = secondCardRef.current;
    if (!section || !stack || !firstCard || !secondCard) return;

    gsap.registerPlugin(ScrollTrigger);

    let cancelled = false;

    // gsap.matchMedia scopes the pin to desktop widths and reverts it on
    // resize. The full-height, overlapping layout it animates is itself gated
    // on `motion-safe:lg:` in the markup, so whenever this animation is not
    // built — narrow screens, or a reduced-motion preference — the two cards
    // fall back to the plain stacked flex column that is the default markup.
    const mm = gsap.matchMedia();

    mm.add(
      "(min-width: 1024px) and (prefers-reduced-motion: no-preference)",
      () => {
        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            // Normally the section is pinned from its own top. On short
            // windows, where the heading plus the stack cannot fit one
            // viewport, anchoring to the bottom instead lets the heading run
            // slightly off the top rather than clipping the cards themselves.
            start: () =>
              section.offsetHeight > window.innerHeight
                ? "bottom bottom"
                : "top top",
            // The section holds the viewport for this whole distance: the
            // second card spends the first 80% of it travelling up, and the
            // remaining 20% is a hold so it is fully settled on top of the
            // first card before the page scrolls on to the next section.
            end: () => `+=${window.innerHeight * 0.85}`,
            // A lower scrub value keeps the timeline closer to the live
            // scroll position. `scrub: 1` let it lag up to a full second
            // behind the scrollbar, which read as the animation being stuck;
            // `0.3` still smooths out a jittery trackpad without the delay.
            scrub: 0.3,
            pin: true,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        });

        timeline
          .fromTo(
            secondCard,
            {
              // Start just past the bottom of the viewport. While pinned the
              // stack is centred, so its top sits at (vh - stackHeight) / 2 and
              // the second card's own resting top another CARD_PEEK below that
              // — the remaining distance down to the fold is what we translate
              // away as the card rises into place.
              y: () =>
                (window.innerHeight + stack.offsetHeight) / 2 - CARD_PEEK,
            },
            { y: 0, ease: "none", duration: 0.8 },
            0,
          )
          // The first card sinks back behind the arriving one. Scaling from
          // its top edge keeps the peeking strip anchored while insetting it
          // slightly, so the pair reads as a stack of cards.
          .fromTo(
            firstCard,
            { scale: 1, opacity: 1 },
            { scale: 0.96, opacity: 0.75, ease: "none", duration: 0.8 },
            0,
          )
          .to({}, { duration: 0.2 });

        ScrollTrigger.refresh();

        // When the context reverts — a resize down to phone widths, or the
        // reduced-motion preference flipping — strip the inline styles GSAP
        // wrote. Reverting a fromTo restores its *start* value rather than a
        // clean slate, which leaves the second card translated a viewport
        // below its slot: in the mobile column that reads as a missing card.
        // This has to run after the revert finishes, hence the next frame.
        // Both cards are styled entirely by classes, so dropping the whole
        // style attribute is safe.
        return () => {
          requestAnimationFrame(() => {
            firstCard.removeAttribute("style");
            secondCard.removeAttribute("style");
          });
        };
      },
    );

        // This section's own header uses the same custom OTF fonts
    // (font-display: swap), and its unsized preview images (figure.png,
    // image-38.png, etc.) can still be loading when the ScrollTrigger above
    // is first built, both of which reflow the page afterward. `start`/`end`
    // above are already live callbacks (re-measuring `section.offsetHeight`/
    // `window.innerHeight` on every refresh), so a plain refresh — once
    // fonts are done loading — is enough to correct them; no need to wait on
    // `window` "load", which blocks on every asset on the whole page.
    if (typeof document !== "undefined" && "fonts" in document) {
      document.fonts.ready.then(() => {
        if (!cancelled) ScrollTrigger.refresh();
      });
    }

    return () => {
      cancelled = true;
      mm.revert();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-[#0061fe] pt-[90px] sm:py-[90px] text-white motion-safe:lg:flex motion-safe:lg:min-h-screen motion-safe:lg:items-center motion-safe:lg:pb-0 motion-safe:lg:pt-24"
      aria-labelledby="business-organization-heading"
    >
      {/* The dark backdrop the cards sit on. Its fixed pixel heights are tuned
          for the section's natural ~800px height; once pinned the section is a
          full viewport tall, so on large screens the heights become relative
          instead — ending at 72% / 88% of the section reproduces the same
          proportions and keeps the blue showing only as a band at the bottom. */}
      <div className="pointer-events-none absolute left-1/2 top-[-359px] h-[932px] w-[min(170vw,max(2435px,100vw))] -translate-x-1/2 rounded-[50%] bg-black blur-[87px] motion-safe:lg:h-[calc(72%_+_359px)]" />
      <div className="pointer-events-none absolute left-1/2 top-[-289px] h-[994px] w-[min(170vw,max(2435px,100vw))] -translate-x-1/2 rounded-[50%] bg-black blur-[222px] motion-safe:lg:h-[calc(88%_+_289px)]" />
      <div className="relative mx-auto flex w-full max-w-[1440px] flex-col px-4 sm:px-[max(24px,7.5vw)] pb-20 motion-safe:lg:pb-0">
        <header className="mx-auto flex max-w-[1224px] flex-col items-center gap-4 text-center">
          <span className="animate-revolving-border rounded-[30px] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23px] text-white backdrop-blur-[3.5px]">
            Structured Support
          </span>
          <motion.h1
            id="business-organization-heading"
            className="max-w-[766px] mb-5 [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-3xl leading-[1.2] sm:text-4xl lg:text-5xl lg:leading-[64px] font-normal text-white"
            variants={headingVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            We help you stay organized, wherever your business is today
          </motion.h1>
          {/* Breathing room below the heading, dropped while the section is
              pinned so the taller card stack still fits one viewport. */}
          <div className="h-8 motion-safe:lg:h-0" />
        </header>
        <div
          ref={stackRef}
          className="relative flex flex-col gap-4 sm:gap-6 motion-safe:lg:block motion-safe:lg:h-[484px]"
        >
          <div
            ref={firstCardRef}
            className="h-auto lg:h-[440px] z-10 motion-safe:lg:absolute motion-safe:lg:inset-x-0 motion-safe:lg:top-0 motion-safe:lg:origin-top motion-safe:lg:will-change-transform"
          >
            <OverviewCard
              title="Starting a New Company?"
              description="We help you set up your company the right way from day one, with formation, documents, registered address, and compliance guidance."
              benefits={newCompanyBenefits}
              buttonLabel="Start your company"
              preview={<CompanyPreview />}
              titleClassName="text-2xl leading-[30px] sm:text-[38px] lg:text-[44px] lg:mt-[41px] lg:leading-[52px]"
            />
          </div>
          <div
            ref={secondCardRef}
            className="h-auto lg:h-[412px] z-10 motion-safe:lg:absolute motion-safe:lg:inset-x-0 motion-safe:lg:top-[44px] motion-safe:lg:z-20 motion-safe:lg:h-[440px] motion-safe:lg:will-change-transform"
          >
            <OverviewCard
              title="Already in Business?"
              description="We help you manage the ongoing financial and compliance work, from bookkeeping and accounting to tax filing and company maintenance."
              benefits={establishedBusinessBenefits}
              buttonLabel="Manage your company"
              preview={<TransactionPreview />}
              titleClassName="text-2xl leading-[30px] sm:text-4xl lg:text-5xl lg:leading-[52px]"
            />
          </div>
        </div>
        <img
          className="pointer-events-none 3xl:right-20 2xl:right-10  z-20 absolute right-[18px] top-[110px] hidden h-[161px] w-[174px] animate-float object-cover lg:block"
          alt="Image"
          src="/home-v3/image-38.png"
        />
        <img
          className="pointer-events-none z-20 2xl:left-20 3xl:left-32 absolute -bottom-[15px] left-[43px] hidden h-[126px] w-[126px] animate-rotate-tilt object-cover lg:block"
          alt="Image"
          src="/home-v3/image-39.png"
        />


        <img
          className="pointer-events-none absolute 3xl:-bottom-96 -bottom-48 left-1/2 h-[206px] w-[262px] -translate-x-1/2"
          alt=""
          src="/home-v3/effect.svg"
        />
        <div className="pointer-events-none absolute bottom-[-140px] 2xl:bottom-[-200px] 3xl:bottom-[-400px] z-0 left-[52.5%] h-[174px] w-[136px] -translate-x-1/2 bg-[linear-gradient(270deg,rgba(197,238,250,0.5)_0%,rgba(197,238,250,1)_44%,rgba(197,238,250,0.5)_100%)] blur-[37px]" />
        <div className="pointer-events-none absolute bottom-[-140px] 2xl:bottom-[-200px] 3xl:bottom-[-400px] z-0 left-[51%] h-36 w-[34px] -translate-x-1/2 bg-[linear-gradient(187deg,rgba(197,238,250,0)_0%,rgba(255,255,255,1)_46%,rgba(255,255,255,0.5)_100%)] blur-[5px]" />
      </div>
    </section>
  );
};
